'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import type { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';

type RuntimeContext = {
    companyId?: string;
    domain?: string;
};

function resolveValue(value: unknown, data: unknown) {
    if (typeof value !== 'string') return value;
    const token = value.match(/^\{\{\s*([^}]+?)\s*\}\}$/)?.[1];
    if (!token) return value;
    return token.split('.').reduce<unknown>((current, key) => {
        return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined;
    }, data) ?? value;
}

export function DeclarativeStructureRenderer({ node, data }: { node: ComponentNode; data?: unknown }) {
    const content = resolveValue(node.data, data);
    const style = convertStylesToInline(node.styles);
    const children = node.components?.map((child) => <DeclarativeStructureRenderer key={child.id} node={child} data={data} />);

    if (node.type === 'IMAGE') {
        return (
            <div style={{ position: 'relative', ...style }}>
                <Image src={assetUrl(String(content ?? ''))} alt="" fill style={{ objectFit: 'contain' }} unoptimized />
            </div>
        );
    }
    if (node.type === 'LINK') {
        return <a href={String(resolveValue(node.href, data) ?? '#')} style={style}>{String(content ?? '')}{children}</a>;
    }
    if (node.type === 'SEARCH_FORM') {
        return <form action="/search" method="get" style={style}><input name="query" placeholder={String(content ?? 'Search')} aria-label="Search" style={{ flex: 1, border: 0, outline: 0, background: 'transparent' }} /><button type="submit">-&gt;</button></form>;
    }
    if (node.type === 'H1' || node.type === 'H2' || node.type === 'H3' || node.type === 'H4') {
        const Heading = ({ H1: 'h1', H2: 'h2', H3: 'h3', H4: 'h4' } as const)[node.type];
        return <Heading style={style}>{String(content ?? '')}{children}</Heading>;
    }
    if (node.type === 'P') return <p style={style}>{String(content ?? '')}{children}</p>;
    if (node.type === 'TEXT') return <span style={style}>{String(content ?? '')}{children}</span>;
    if (node.type === 'SECTION') return <section style={style}>{children}</section>;
    return <div style={style}>{content != null && node.type !== 'CONTAINER' && node.type !== 'GRID' && node.type !== 'CARD' && node.type !== 'ROW' && node.type !== 'COLUMN' ? String(content) : null}{children}</div>;
}

type Offer = {
    id?: string | number;
    merchant?: { merchant_name?: string; promotional_tag?: string };
    offer?: {
        offer_title?: string;
        offer_type?: { name?: string };
        product_image?: string;
        banner_image?: string;
        url?: string;
    };
};

type OffersResponse = {
    data?: { items?: Offer[]; widget?: { widget_heading?: string; widget_text?: string } };
};

const apiBaseUrl = 'https://api.aff.coderlabdev.com';
const assetBaseUrl = 'https://affiliatespublishers.com';

function assetUrl(value?: string) {
    if (!value) return '';
    return /^https?:\/\//i.test(value) ? value : `${assetBaseUrl.replace(/\/+$/, '')}/${value.replace(/^\/+/, '')}`;
}

function valueAt(source: unknown, path: string, fallback: string) {
    const value = path.split('.').reduce<unknown>((current, key) => {
        return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined;
    }, source);
    return value == null || value === '' ? fallback : String(value);
}

export function BuilderHeaderRuntime({ structure, componentStyles }: { structure?: ComponentNode; componentStyles?: Record<string, any> }) {
    const links = ['Categories', 'Stores', 'Products', 'Events', 'Promotions', 'Blog'];
    const headerStyles = componentStyles ? convertStylesToInline(componentStyles) : {};

    // When structure exists, check if it's a wrapper CONTAINER with no styles
    if (structure) {
        // If structure is a CONTAINER with no styles, render children directly to avoid extra wrapper
        if (structure.type === 'CONTAINER' && (!structure.styles || Object.keys(structure.styles).length === 0)) {
            return (
                <header style={headerStyles}>
                    {structure.components?.map((child) => (
                        <DeclarativeStructureRenderer key={child.id} node={child} />
                    ))}
                </header>
            );
        }
        // Otherwise render the structure normally (it has meaningful styles)
        return (
            <header style={headerStyles}>
                <DeclarativeStructureRenderer node={structure} />
            </header>
        );
    }

    // Fallback: hardcoded header with default styles
    return (
        <header style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '14px 24px', borderBottom: '1px solid #ded3c3', background: '#fffdf8', color: '#253034', flexWrap: 'wrap', ...headerStyles }}>
            <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'inherit', textDecoration: 'none', fontWeight: 600 }}>
                <span style={{ display: 'grid', placeItems: 'center', width: 28, height: 28, background: '#f06445', color: '#fff', fontSize: 10 }}>GT</span>
                GetTopDiscounts
            </Link>
            <form action="/search" method="get" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 260px', maxWidth: 380, height: 38, padding: '0 12px', border: '1px solid #e8b8aa', borderRadius: 22 }}>
                <input name="query" placeholder="Search stores, categories..." aria-label="Search stores and categories" style={{ minWidth: 0, flex: 1, border: 0, outline: 0, background: 'transparent', fontSize: 11 }} />
                <button type="submit" aria-label="Submit search" style={{ border: 0, borderRadius: 99, background: '#f06445', color: '#fff', width: 25, height: 25 }}>-&gt;</button>
            </form>
            <nav style={{ display: 'flex', gap: 16, marginLeft: 'auto', fontSize: 11, flexWrap: 'wrap' }}>
                <Link href="/" style={{ color: '#f06445' }}>Home</Link>
                {links.map((link) => <Link href={`/${link.toLowerCase()}`} key={link} style={{ color: '#8b5147', textDecoration: 'none' }}>{link}</Link>)}
            </nav>
        </header>
    );
}

export function BuilderFooterRuntime({ structure, componentStyles }: { structure?: ComponentNode; componentStyles?: Record<string, any> }) {
    const groups = {
        Company: [['Home', '/'], ['Stores', '/all-stores/A'], ['Categories', '/category'], ['Contact Us', '/contact-us']],
        Information: [['About Us', '/about-us'], ['Privacy Policy', '/privacy-policy'], ['Terms of Services', '/terms-of-services'], ['Affiliate Disclosure', '/affiliate-disclosure']],
        Explore: [['Events', '/events'], ['Promotions', '/promotion'], ['Products', '/products'], ['Search', '/search']],
    };
    const footerStyles = componentStyles ? convertStylesToInline(componentStyles) : {};

    // When structure exists, check if it's a wrapper CONTAINER with no styles  
    if (structure) {
        // If structure is a CONTAINER with no styles, render children directly to avoid extra wrapper
        if (structure.type === 'CONTAINER' && (!structure.styles || Object.keys(structure.styles).length === 0)) {
            return (
                <footer style={footerStyles}>
                    {structure.components?.map((child) => (
                        <DeclarativeStructureRenderer key={child.id} node={child} />
                    ))}
                </footer>
            );
        }
        // Otherwise render the structure normally (it has meaningful styles)
        return (
            <footer style={footerStyles}>
                <DeclarativeStructureRenderer node={structure} />
            </footer>
        );
    }

    // Fallback: hardcoded footer with default styles
    return (
        <footer style={{ background: '#202a2d', color: '#dce4df', ...footerStyles }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(3, minmax(0, 1fr))', gap: 24, padding: '32px 28px' }}>
                <div><strong style={{ color: '#fffdf8' }}>GetTopDiscounts</strong><p style={{ maxWidth: 220, color: '#99a8a6', fontSize: 10, lineHeight: 1.5 }}>Your smart shopping companion for verified coupons and exclusive deals.</p></div>
                {Object.entries(groups).map(([title, links]) => <div key={title}><strong style={{ display: 'block', marginBottom: 10, color: '#fffdf8', fontSize: 10, textTransform: 'uppercase' }}>{title}</strong>{links.map(([label, href]) => <Link href={href} key={href} style={{ display: 'block', marginBottom: 8, color: '#aab7b3', fontSize: 10, textDecoration: 'none' }}>{label}</Link>)}</div>)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 28px', borderTop: '1px solid #394648', color: '#82918e', fontSize: 9 }}><span>100% verified savings</span><span>Shop with confidence</span><span>© 2026 GetTopDiscounts</span></div>
        </footer>
    );
}

export function BuilderOffersRuntime({ component, context }: { component: ComponentNode; context?: RuntimeContext }) {
    const [offers, setOffers] = useState<Offer[]>([]);
    const [widget, setWidget] = useState<OffersResponse['data']>({});
    const [loading, setLoading] = useState(true);
    const apiConfig = component.apiConfig;
    const presentation = component.presentation ?? {};

    useEffect(() => {
        if (!apiConfig?.apiEndpoint) return;
        const controller = new AbortController();
        const load = async () => {
            try {
                const endpoint = /^https?:\/\//i.test(apiConfig.apiEndpoint) ? apiConfig.apiEndpoint : `${apiBaseUrl.replace(/\/+$/, '')}/${apiConfig.apiEndpoint.replace(/^\/+/, '')}`;
                const body = new FormData();
                Object.entries(apiConfig.body ?? {}).forEach(([key, value]) => body.append(key, String(value)));
                if (context?.companyId && !apiConfig.body?.company_id) body.append('company_id', context.companyId);
                if (context?.domain) body.append('domain', context.domain);
                const response = await fetch(endpoint, { method: apiConfig.method, body, signal: controller.signal });
                const payload = await response.json() as OffersResponse;
                setOffers(payload.data?.items ?? []);
                setWidget(payload.data ?? {});
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        void load().catch(() => setLoading(false));
        return () => controller.abort();
    }, [apiConfig, context?.companyId, context?.domain]);

    const heading = valueAt({ data: { widget } }, presentation.widgetHeadingPath || 'data.widget.widget_heading', 'Beyond the coupon');
    const description = valueAt({ data: { widget } }, presentation.widgetTextPath || 'data.widget.widget_text', 'Verified offers from trusted brands.');
    const visibleOffers = offers.slice(0, component.totalCards ?? 6);

    return (
        <section style={{ width: '100%', padding: '24px', background: '#f9f5ed', color: '#253034' }}>
            <span style={{ display: 'block', color: '#a65b47', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em' }}>POPULAR OFFERS</span>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'end' }}><h2 style={{ margin: '4px 0 8px', fontSize: 21, fontWeight: 500 }}>{heading}</h2><Link href="/offers" style={{ color: '#5c7897', fontSize: 10 }}>View all deals -&gt;</Link></div>
            <p style={{ maxWidth: 650, margin: '0 0 16px', color: '#748083', fontSize: 10, lineHeight: 1.5 }}>{description}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                {loading && [0, 1, 2].map((item) => <div key={item} style={{ height: 176, border: '1px solid #ded3c3', background: '#eee8de' }} />)}
                {!loading && visibleOffers.map((item, index) => {
                    const merchant = valueAt(item, presentation.cardMerchantPath || 'merchant.merchant_name', 'Verified merchant');
                    const title = valueAt(item, presentation.cardTitlePath || 'offer.offer_title', 'Verified offer');
                    const badge = valueAt(item, presentation.cardBadgePath || 'merchant.promotional_tag', 'Offer');
                    const meta = valueAt(item, presentation.cardMetaPath || 'offer.offer_type.name', 'Offer');
                    const image = assetUrl(valueAt(item, presentation.cardImagePath || 'offer.product_image', ''));
                    const href = assetUrl(valueAt(item, presentation.cardCtaPath || 'offer.url', ''));
                    return <article key={item.id ?? index} style={{ overflow: 'hidden', border: '1px solid #ded3c3', background: '#fffdf8' }}><div style={{ position: 'relative', display: 'grid', placeItems: 'center', height: 112, background: '#e8e2d8' }}>{image && <div style={{ position: 'relative', width: '78%', height: '100%' }}><Image src={image} alt={`${merchant} offer`} fill style={{ objectFit: 'contain' }} unoptimized /></div>}<span style={{ position: 'absolute', top: 8, left: 8, padding: '4px 6px', background: '#f06445', color: '#fffdf8', fontFamily: 'monospace', fontSize: 9 }}>{badge}</span></div><div style={{ padding: 12 }}><small style={{ color: '#a65b47', fontFamily: 'monospace', fontSize: 9, textTransform: 'uppercase' }}>{merchant}</small><strong style={{ display: 'block', minHeight: 32, marginTop: 4, fontSize: 12 }}>{title}</strong><div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 10 }}><span style={{ color: '#748083' }}>{meta}</span><a href={href || '#'}>{href ? 'View details ->' : 'Text link'}</a></div></div></article>;
                })}
            </div>
        </section>
    );
}
